import React, { useContext } from 'react';
import { BluePill, WarningPill } from '../DropdownEditor/CustomOptions';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { IBinLocation, IProduct, IProductWarehouseDetail, Maybe } from '../../graphql/schema/p4types';
import { IDropdownOptionRenderer } from '../../@types/entityTypes';
import { Tooltip } from '@fluentui/react-components';
import styled from 'styled-components';

interface StockPillProps {
  warehouseFreeStock: Maybe<number>;
  tooltipText: Maybe<string>;
}

interface ObsoletePillProps {
  obsolete: boolean;
}

const OptionContainer = styled.div`
  display: flex;
  gap: 4px;
  justify-content: space-between;
`;

const PillContainer = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end'
`;

function StockPill({ warehouseFreeStock, tooltipText }: StockPillProps): JSX.Element {
  let totalFreeStockText = null;

  return (
    <div>
      {warehouseFreeStock !== null && warehouseFreeStock !== undefined ? (
        <Tooltip relationship={'label'} content={tooltipText}>
          <BluePill style={{ float: 'right', minWidth: '40px', textAlign: 'center' }}>
            {warehouseFreeStock}
            {totalFreeStockText}
          </BluePill>
        </Tooltip>
      ) : null}
    </div>
  );
}

function ObsoletePill({ obsolete }: ObsoletePillProps): JSX.Element {
  return (
    <>
      {obsolete ? (
        <div>
          <WarningPill>{'OBSOLETE'}</WarningPill>
        </div>
      ) : null}
    </>
  );
}

//This content gets rendered to document.body, so can't rely on useContext, hence optionally passing values through as a prop.
export const ProductOptionRenderer: IDropdownOptionRenderer = ({
  option,
  values,
}: {
  option: IProduct;
  values?: any;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const description: string = `${option.reference} ${option.description}`;
  const formValues: Maybe<any> = connectedForm.values ?? values;
  const warehouseId: Maybe<number> = formValues?.warehouse?.id ?? formValues?.warehouseFrom?.id;
  const freeStock: number =
    (warehouseId &&
      option.productWarehouseDetails?.find((w: IProductWarehouseDetail) => w.warehouse?.id === warehouseId)
        ?.freeStock) ??
    null;

  return (
    <OptionContainer>
      {description}
      <PillContainer>
        <StockPill warehouseFreeStock={freeStock} tooltipText={'Free stock in selected warehouse'} />
        {freeStock !== option?.freeStock && (
          <StockPill warehouseFreeStock={option?.freeStock} tooltipText={'Free stock across all warehouses'} />
        )}
        <ObsoletePill obsolete={option?.obsolete ?? false} />
      </PillContainer>
    </OptionContainer>
  );
};

export const BinLocationFromOptionRenderer: IDropdownOptionRenderer = ({
  option,
}: {
  option: IBinLocation & { level?: number; freeStock?: number };
}) => {
  const location: Maybe<string> = option?.location;
  const freeStock: Maybe<number> = option?.freeStock;
  const level: Maybe<number> = option?.level;

  return (
    <OptionContainer>
      {location}
      <PillContainer>
        <StockPill warehouseFreeStock={freeStock || level} tooltipText={'Free stock in selected bin'} />
      </PillContainer>
    </OptionContainer>
  );
};
